import type { SidebarMenuItem } from '@nebula-layout/layout';
import {
  flattenVaultMenuRouteKeys,
  normalizeVaultMenuRouteKey,
  VAULT_MENU_CANONICAL,
} from './vault-menu-canonical.snapshot';
import type { VaultSidebarMenuItem } from './vault-sidebar-menu.types';
import {
  getQaFallbackAllowedRouteKeys,
  resolveVaultQaRoleProfileForRestrictions,
  userIsSuperadminFromSession,
} from './vault-role-menu-matrix.util';
import { isVaultOperativeScopeHydrated } from '../vault-operative-role.util';

let lastAllowedRouteKeys = new Set<string>();
let permissionsState: 'pending' | 'ready' = 'pending';

export function setVaultSidebarAllowedRouteKeys(keys: Set<string>): void {
  lastAllowedRouteKeys = new Set(keys);
  permissionsState = 'ready';
}

export function resetVaultSidebarPermissionsPending(): void {
  lastAllowedRouteKeys = new Set();
  permissionsState = 'pending';
}

export function getVaultSidebarAllowedRouteKeys(): Set<string> {
  return new Set(lastAllowedRouteKeys);
}

export function vaultSidebarPermissionsReady(): boolean {
  return permissionsState === 'ready';
}

/** true = el usuario NO puede acceder (mostrar toast / bloquear clic). */
export function shouldBlockVaultRouteAccess(route: string): boolean {
  if (userIsSuperadminFromSession()) return false;
  if (resolveVaultQaRoleProfileForRestrictions() === 'admin') return false;

  const key = normalizeVaultMenuRouteKey(route);
  if (!key) return true;

  if (permissionsState === 'pending') {
    if (isVaultOperativeScopeHydrated()) {
      const profile = resolveVaultQaRoleProfileForRestrictions();
      if (profile !== 'unknown' && profile !== 'admin') {
        const fallback = getQaFallbackAllowedRouteKeys(profile);
        return !isRouteKeyAllowedStrict(key, fallback);
      }
    }
    return false;
  }

  if (lastAllowedRouteKeys.size === 0) {
    return key !== normalizeVaultMenuRouteKey('/layout/vault/dashboard');
  }

  return !isRouteKeyAllowedStrict(key, lastAllowedRouteKeys);
}

export function isVaultSidebarRouteAllowed(route: string): boolean {
  return !shouldBlockVaultRouteAccess(route);
}

/** Menú completo visible; sin marcas de restricción en la UI. */
export function cloneVaultMenuForDisplay(items: VaultSidebarMenuItem[]): VaultSidebarMenuItem[] {
  const cloned = items.map((item) => ({
    ...item,
    vaultRestricted: false,
    children: item.children?.length ? cloneVaultMenuForDisplay(item.children) : item.children ?? [],
  }));
  return cloned;
}

export function mergeVaultSidebarWithRestrictions(
  canonical: VaultSidebarMenuItem[],
  allowed: Set<string>,
): VaultSidebarMenuItem[] {
  setVaultSidebarAllowedRouteKeys(allowed);
  return cloneVaultMenuForDisplay(canonical);
}

/** Extrae rutas permitidas desde el árbol devuelto por el API. */
export function collectAllowedRouteKeysFromApiMenu(menu: SidebarMenuItem[]): Set<string> {
  return flattenVaultMenuRouteKeys(menu as VaultSidebarMenuItem[]);
}

function vaultModuleHead(routeKey: string): string {
  const rest = routeKey.replace(/^\/layout\/vault\/?/, '').split('/').filter(Boolean);
  return rest[0] ?? '';
}

export function isRouteKeyAllowedStrict(routeKey: string, allowed: Set<string>): boolean {
  if (allowed.has(routeKey)) return true;

  const routeHead = vaultModuleHead(routeKey);

  if (routeKey.includes('/layout/vault/vault/')) {
    const ingestAllowed =
      allowed.has(normalizeVaultMenuRouteKey('/layout/vault/vault/projects')) ||
      allowed.has(normalizeVaultMenuRouteKey('/layout/vault/vault'));
    if (ingestAllowed) return true;
  }

  // Sidebar colapsa .../vault/vault/... → .../vault/... al validar clic (#0000149).
  if (
    routeKey === normalizeVaultMenuRouteKey('/layout/vault/projects') ||
    routeKey.startsWith('/layout/vault/projects/')
  ) {
    const ingestAllowed =
      allowed.has(normalizeVaultMenuRouteKey('/layout/vault/vault/projects')) ||
      allowed.has(normalizeVaultMenuRouteKey('/layout/vault/vault'));
    if (ingestAllowed) return true;
  }

  if (routeKey.startsWith('/layout/vault/settings')) {
    return [...allowed].some((k) => k.startsWith('/layout/vault/settings'));
  }

  if (routeKey.startsWith('/layout/vault/admin/')) {
    return [...allowed].some((k) => k.startsWith('/layout/vault/admin/'));
  }

  if (!routeHead) return false;

  for (const allowedKey of allowed) {
    if (vaultModuleHead(allowedKey) === routeHead) return true;
  }

  return false;
}

export function resolveVaultSidebarAllowedRouteKeys(
  apiAllowed: Set<string>,
  apiTree: SidebarMenuItem[],
): Set<string> {
  if (userIsSuperadminFromSession()) {
    return flattenVaultMenuRouteKeys(VAULT_MENU_CANONICAL);
  }

  let allowed = new Set(apiAllowed);
  if (allowed.size === 0 && apiTree.length > 0) {
    allowed = collectAllowedRouteKeysFromApiMenu(apiTree);
  }
  if (allowed.size === 0) {
    const fallback = getQaFallbackAllowedRouteKeys(resolveVaultQaRoleProfileForRestrictions());
    if (fallback.size > 0) allowed = fallback;
  }
  if (allowed.size === 0) {
    allowed = new Set([normalizeVaultMenuRouteKey('/layout/vault/dashboard')].filter(Boolean));
  }
  return allowed;
}

export function vaultMenuCoversCanonical(
  apiMenu: SidebarMenuItem[],
  minCoverageRatio = 0.75,
): boolean {
  const apiKeys = collectAllowedRouteKeysFromApiMenu(apiMenu);
  const canonicalKeys = flattenVaultMenuRouteKeys(VAULT_MENU_CANONICAL);
  if (canonicalKeys.size === 0) return apiKeys.size > 0;
  let matched = 0;
  for (const key of canonicalKeys) {
    if (apiKeys.has(key)) matched++;
  }
  return matched / canonicalKeys.size >= minCoverageRatio;
}
